// Import type definitions for Tag and TagMatch data structures
import { Tag, TagMatch } from '../types';
// Import unified API service for real tag operations
import { createUnifiedApiService } from './unifiedApiService';
// Import auth context to get current user
import { useAuth } from '../contexts/AuthContext';

/**
 * TagService Interface
 * This defines all the functions this service must provide
 * Think of it like a contract - any TagService implementation must have these methods
 */
interface TagService {
  getAvailableTags: () => Promise<Tag[]>;     // Get all unassigned RFID tags
  getTagMatches: () => Promise<TagMatch[]>;   // Get all tag-to-product associations
  isTagAvailable: (tagNumber: string) => Promise<boolean>; // Check if a tag can be used
  getTagStatus: (tagNumber: string) => Promise<{available: boolean; reason?: string; matchedTo?: string}>; // Detailed tag status
  createTagMatch: (productId: string, tagNumber: string, quantity: number, matchedBy: string) => Promise<TagMatch>; // Create new tag association
  validateTagNumber: (tagNumber: string) => boolean; // Check if tag number format is valid
  getMatchedTags: () => Promise<string[]>;    // Get list of all currently assigned tags
}

/**
 * TagServiceImpl Class
 * This is the actual implementation of the TagService
 * It handles all RFID tag-related operations in the application using the unified API
 */
class TagServiceImpl implements TagService {
  private user: any = null; // Current user from auth context

  constructor() {
    // Try to get current user - this will be set when tagService is initialized
    // In a real app, this would come from dependency injection or context
  }

  // Set current user for API calls
  setUser(user: any) {
    this.user = user;
  }

  /**
   * getAvailableTags Method
   * Returns all RFID tags that are not currently assigned to products
   * Used when showing users which tags they can choose from
   */
  async getAvailableTags(): Promise<Tag[]> {
    // This method requires API implementation
    // Currently not implemented as it requires specific API endpoints
    throw new Error('getAvailableTags not implemented - requires API endpoint');
  }

  async getTagMatches(): Promise<TagMatch[]> {
    // This method requires API implementation
    // Currently not implemented as it requires specific API endpoints
    throw new Error('getTagMatches not implemented - requires API endpoint');
  }

  /**
   * isTagAvailable Method
   * Checks if a specific RFID tag can be used for a new product
   * Returns true if available, false if already in use
   */
  async isTagAvailable(tagNumber: string): Promise<boolean> {
    // Use getTagStatus to check availability via API
    try {
      const status = await this.getTagStatus(tagNumber);
      return status.available;
    } catch (error) {
      console.error('Error checking tag availability:', error);
      throw error;
    }
  }

  async getTagStatus(tagNumber: string): Promise<{available: boolean; reason?: string; matchedTo?: string}> {
    try {
      console.log(`🔍 TagService getTagStatus: checking tag ${tagNumber}`);
      
      // Check internet connection
      if (!navigator.onLine) {
        throw new Error('No internet connection. Please check your connection and try again.');
      }

      // Online - use API
      if (!this.user) {
        throw new Error('No user context available for API call');
      }

      const apiService = createUnifiedApiService(this.user);
      const tagInfo = await apiService.scanTag(tagNumber);
      
      if (tagInfo) {
        // Tag exists and is matched
        return {
          available: false,
          reason: 'Tag is already matched',
          matchedTo: tagInfo.productId || 'Unknown product'
        };
      } else {
        // Tag not found - available for matching
        return {
          available: true
        };
      }
    } catch (error) {
      console.error('❌ TagService getTagStatus error:', error);
      throw error;
    }
  }

  async createTagMatch(productId: string, tagNumber: string, quantity: number, matchedBy: string): Promise<TagMatch> {
    try {
      console.log(`🏷️ TagService createTagMatch: ${tagNumber} → ${productId} (qty: ${quantity})`);
      
      // Check internet connection first
      if (!navigator.onLine) {
        throw new Error('No internet connection. Please check your connection and try again.');
      }

      // Must have user context for API call
      if (!this.user) {
        throw new Error('No user context available for API call');
      }

      const apiService = createUnifiedApiService(this.user);
      
      // Create the tag match via API
      const unifiedTagInfo = await apiService.createTagMatch(productId, tagNumber, quantity);
      
      // Add delay before verification to handle backend propagation
      console.log('⏳ Waiting for backend propagation...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Verify the tag was actually created in the backend with retry logic
      console.log('🔍 Verifying tag creation in backend...');
      let verificationResult = await apiService.scanTag(tagNumber);
      
      // Log what we got back for debugging
      console.log('📋 Verification attempt 1:', {
        found: !!verificationResult,
        expectedProductId: productId,
        actualProductId: verificationResult?.productId,
        rawResult: verificationResult
      });
      
      // Type-safe comparison - convert both to strings
      const expectedId = String(productId);
      let actualId = verificationResult?.productId ? String(verificationResult.productId) : null;
      
      // If first verification fails, retry once more after additional delay
      if (!verificationResult || actualId !== expectedId) {
        console.log('🔄 First verification failed, retrying after delay...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        verificationResult = await apiService.scanTag(tagNumber);
        actualId = verificationResult?.productId ? String(verificationResult.productId) : null;
        
        console.log('📋 Verification attempt 2:', {
          found: !!verificationResult,
          expectedProductId: expectedId,
          actualProductId: actualId,
          rawResult: verificationResult
        });
      }
      
      // Final verification check
      if (!verificationResult) {
        console.error('❌ Tag not found in backend after creation');
        throw new Error('Tag creation reported success but tag not found in backend. Please refresh and check your tags.');
      }
      
      if (actualId !== expectedId) {
        // Tag exists but with different product ID - this might be a data issue
        console.warn('⚠️ Tag found but productId mismatch:', {
          expected: expectedId,
          actual: actualId,
          tagNumber: tagNumber
        });
        // For now, we'll consider this a success with warning since the tag was created
        console.log('⚠️ Tag verified with warning - productId mismatch but tag exists');
      } else {
        console.log('✅ Tag fully verified in backend');
      }
      
      // Convert UnifiedTagInfo to TagMatch format for backward compatibility
      const newMatch: TagMatch = {
        id: unifiedTagInfo.id,
        productId: unifiedTagInfo.productId!,
        tagNumber: unifiedTagInfo.rfidNumber,
        quantity: unifiedTagInfo.quantity,
        matchedBy,
        matchedAt: unifiedTagInfo.matchedAt!,
        status: unifiedTagInfo.status === 'on_hand' ? 'on_hand' : 'pending',
        notes: unifiedTagInfo.notes || 'Created via Smart API',
        location: unifiedTagInfo.location || undefined
      };
      
      console.log('✅ Successfully created and verified tag match:', newMatch.tagNumber, '→', newMatch.productId);
      return newMatch;
      
    } catch (error) {
      console.error('❌ TagService createTagMatch error:', error);
      
      // If it's a duplicate tag error, re-throw with clear message
      if (error instanceof Error && (
        error.message.includes('already matched') || 
        error.message.includes('already exists') ||
        error.message.includes('duplicate')
      )) {
        console.log('🚫 Duplicate tag detected');
        throw error;
      }
      
      // Re-throw all errors - no offline fallback
      throw error;
    }
  }

  validateTagNumber(tagNumber: string): boolean {
    // RFID tag format: 12-character alphanumeric
    const tagRegex = /^[A-Z0-9]{12}$/;
    return tagRegex.test(tagNumber.toUpperCase());
  }

  async getMatchedTags(): Promise<string[]> {
    // This method requires API implementation
    // Currently not implemented as it requires specific API endpoints
    throw new Error('getMatchedTags not implemented - requires API endpoint');
  }

  // Helper method to format tag number
  formatTagNumber(tagNumber: string): string {
    return tagNumber.toUpperCase().replace(/[^A-Z0-9]/g, '');
  }
}

// Create singleton instance
const tagServiceInstance = new TagServiceImpl();

// Factory function to initialize tagService with user context
export const initializeTagService = (user: any) => {
  tagServiceInstance.setUser(user);
  return tagServiceInstance;
};

// Export the singleton instance (will work with or without user context)
export const tagService = tagServiceInstance;

// Export the class for testing
export { TagServiceImpl };